import { z } from 'zod';

// Programs, cycles and participants (/api/programs). Response types: `X` is the JSON on the wire
// (dates as ISO strings), `XOut` what the service returns before serialisation (dates as Date).

const Timestamp = z.codec(z.iso.datetime({ offset: true }), z.date(), {
  decode: (s) => new Date(s),
  encode: (d) => d.toISOString(),
});

/** A row id as programService checks it before a lookup. */
const RowId = z.string().regex(/^[0-9a-f-]{36}$/i, 'Not an id');

export const ProgramParticipantStatus = z.enum([
  'invited',
  'applied',
  'selected',
  'active',
  'completed',
  'withdrawn',
  'declined',
]);
export type ProgramParticipantStatus = z.infer<typeof ProgramParticipantStatus>;

const StatusList = z
  .string()
  .refine(
    (s) => s === '' || s.split(',').every((v) => (ProgramParticipantStatus.options as readonly string[]).includes(v)),
    'Unknown status',
  );

export const ProgramNoQuery = z.strictObject({});

export const ProgramsListQuery = z.strictObject({ archived: z.string().optional() });
export type ProgramsListQuery = z.input<typeof ProgramsListQuery>;

/** The program by id or key. */
export const ProgramRefParams = z.strictObject({ program: z.string().min(1) });

export const ProgramCycleParams = z.strictObject({ cycleId: RowId });

export const ProgramParticipantParams = z.strictObject({ id: RowId });

/** Paging values that are not numbers fall back to the defaults in the service. */
export const ProgramParticipantsQuery = z.strictObject({
  cycleId: z.union([z.literal(''), RowId]).optional(),
  programId: z.string().optional(),
  status: StatusList.optional(),
  role: z.string().optional(),
  userId: z.string().optional(),
  contactId: z.string().optional(),
  q: z.string().optional(),
  includeDetached: z.string().optional(),
  page: z.string().optional(),
  limit: z.string().optional(),
});
export type ProgramParticipantsQuery = z.input<typeof ProgramParticipantsQuery>;

export const ProgramStatusChangeRequest = z.strictObject({
  status: ProgramParticipantStatus,
  note: z.string().nullable().optional(),
});
export type ProgramStatusChangeRequest = z.input<typeof ProgramStatusChangeRequest>;

/** The person is a member (userId), a contact (contactId) or found by email, in that order. */
export const ProgramAddParticipantRequest = z.strictObject({
  email: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  contactId: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  status: z
    .union([z.literal(''), ProgramParticipantStatus])
    .nullable()
    .optional(),
  note: z.string().nullable().optional(),
});
export type ProgramAddParticipantRequest = z.input<typeof ProgramAddParticipantRequest>;

export const ProgramSummary = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  kind: z.string(),
  description: z.string().nullable(),
  archivedAt: Timestamp.nullable(),
  cycleCount: z.number(),
  participantCount: z.number(),
});
export type ProgramSummary = z.input<typeof ProgramSummary>;
export type ProgramSummaryOut = z.output<typeof ProgramSummary>;

export const ProgramCycleSummary = z.object({
  id: z.string(),
  label: z.string(),
  year: z.number().nullable(),
  state: z.string(),
  opensAt: Timestamp.nullable(),
  closesAt: Timestamp.nullable(),
  settings: z.record(z.string(), z.unknown()),
  byStatus: z.record(z.string(), z.number()),
});
export type ProgramCycleSummary = z.input<typeof ProgramCycleSummary>;

export const ProgramCycleList = z.object({
  program: z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    kind: z.string(),
    description: z.string().nullable(),
  }),
  cycles: z.array(ProgramCycleSummary),
});
export type ProgramCycleList = z.input<typeof ProgramCycleList>;
export type ProgramCycleListOut = z.output<typeof ProgramCycleList>;

export const ProgramPerson = z.object({
  type: z.enum(['member', 'contact', 'detached']),
  id: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  restricted: z.boolean().optional(),
});
export type ProgramPerson = z.infer<typeof ProgramPerson>;

export const ProgramHistoryEntry = z.object({
  from: z.string().nullable(),
  to: z.string(),
  at: z.string(),
  by: z.string().nullable().optional(),
  source: z.string().optional(),
  note: z.string().optional(),
});
export type ProgramHistoryEntry = z.infer<typeof ProgramHistoryEntry>;

/** Viewers get a member under 18 as `restricted`, with the person and metadata redacted. */
export const ProgramParticipant = z.object({
  id: z.string(),
  cycleId: z.string(),
  cycle: z
    .object({
      id: z.string(),
      label: z.string(),
      year: z.number().nullable(),
      state: z.string(),
      program: z.object({ id: z.string(), key: z.string(), name: z.string() }).optional(),
    })
    .optional(),
  person: ProgramPerson,
  role: z.string(),
  status: z.string(),
  statusChangedAt: Timestamp.nullable(),
  joinedAt: Timestamp.nullable(),
  leftAt: Timestamp.nullable(),
  source: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  history: z.array(ProgramHistoryEntry),
  detachedAt: Timestamp.nullable(),
  createdAt: Timestamp,
  restricted: z.boolean().optional(),
});
export type ProgramParticipant = z.input<typeof ProgramParticipant>;
export type ProgramParticipantOut = z.output<typeof ProgramParticipant>;

export const ProgramParticipantPage = z.object({
  participants: z.array(ProgramParticipant),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type ProgramParticipantPage = z.input<typeof ProgramParticipantPage>;
export type ProgramParticipantPageOut = z.output<typeof ProgramParticipantPage>;
